import { useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { useSocket } from './useSocket';

export interface AgentPulse {
  id: string;
  status: string;
  lastActiveAt: string | null;
  level: number; // 0..1 综合活跃度
  recentMessages: number; // 近 10s 消息数
  active: boolean;
}

export interface NexusPulseSnapshot {
  ts: number;
  agents: AgentPulse[];
}

export interface NexusBurst {
  agentId: string;
  kind: 'message' | 'promotion' | 'status';
  ts: number;
}

const POLL_MS = 6000;

export interface UseNexusPulseResult {
  pulses: Map<string, AgentPulse>;
  bursts: NexusBurst[];
  connected: boolean;
}

/**
 * 中枢实时脉冲数据源（F = Real-time Pulse）：
 * - 优先订阅 Socket.IO 的 nexus:pulse（每 2s 一帧，来自 server/socket/nexus.ts）
 * - 监测 recentMessages 由 0 转正时，自动派生 message 爆发，制造「数据流」质感
 * - socket 不可用时降级到 HTTP 轮询 /api/agents/pulse（与 socket 同构）
 * 中枢 globe 与下钻面板均消费本 Hook，保证三端同源
 */
export function useNexusPulse(opts?: { socket?: Socket | null }): UseNexusPulseResult {
  const fallback = useSocket();
  const socket = opts?.socket ?? fallback;
  const [pulses, setPulses] = useState<Map<string, AgentPulse>>(new Map());
  const [bursts, setBursts] = useState<NexusBurst[]>([]);
  const [socketReady, setSocketReady] = useState(false);

  // 跟踪 socket 连接状态：连接成功则信任推送，暂停 HTTP 轮询避免重复采样
  useEffect(() => {
    if (!socket) return;
    const onConnect = () => setSocketReady(true);
    const onDisconnect = () => setSocketReady(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) setSocketReady(true);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  // Socket 实时推送
  useEffect(() => {
    if (!socket) return;
    const apply = (snap: any) => {
      if (!snap || !Array.isArray(snap.agents)) return;
      setPulses((prev) => {
        const next = new Map(prev);
        for (const a of snap.agents as AgentPulse[]) {
          const old = prev.get(a.id);
          if (old && old.recentMessages === 0 && a.recentMessages > 0) {
            setBursts((b) => [...b.slice(-8), { agentId: a.id, kind: 'message', ts: Date.now() }]);
          }
          next.set(a.id, a);
        }
        return next;
      });
    };
    const onPulse = (snap: any) => apply(snap);
    const onBurst = (b: any) => {
      if (b?.agentId) setBursts((prev) => [...prev.slice(-8), { agentId: b.agentId, kind: b.kind || 'message', ts: b.ts || Date.now() }]);
    };
    socket.on('nexus:pulse', onPulse);
    socket.on('nexus:pulse:burst', onBurst);
    socket.emit('nexus:pulse:subscribe');
    return () => {
      socket.off('nexus:pulse', onPulse);
      socket.off('nexus:pulse:burst', onBurst);
    };
  }, [socket]);

  // HTTP 轮询兜底（仅 socket 不可用时）
  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (socketReady) return;
      try {
        const r = await fetch('/api/agents/pulse', { credentials: 'include' });
        if (!r.ok) return;
        const snap = await r.json();
        if (active && snap && Array.isArray(snap.agents)) {
          setPulses((prev) => {
            const next = new Map(prev);
            for (const a of snap.agents as AgentPulse[]) next.set(a.id, a);
            return next;
          });
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [socketReady]);

  return { pulses, bursts, connected: socketReady };
}
